import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import intl from 'react-intl-universal';
import styled from 'src/ui/library/styled';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';

const DATE_INPUT_FORMATS = [
  'YYYY-MM-DD',
  'DD-MMM-YYYY',
  'DD-MMM',
  'M/D/YYYY h:mm:ss A',
  'MM/DD/YYYY hh:mm:ss A',
  'M/D/YYYY',
  'MM/DD/YYYY',
  moment.ISO_8601,
];
const DATE_INPUT_LOCALE_TEXT = {
  fieldMonthPlaceholder: params => (params.format === 'MMM' ? 'MMM' : params.contentType === 'letter' ? 'MMMM' : 'MM'),
};

const getParsedDate = value => {
  if (!value) return null;

  const parsedDate = moment(value, DATE_INPUT_FORMATS, true);
  return parsedDate.isValid() ? parsedDate : null;
};

const normalizeDateBoundaryValue = (value, boundary) => {
  if (!value) return undefined;

  const yearOnly = typeof value === 'number' || /^\d{4}$/.test(value.toString());
  if (yearOnly) return `${value}-${boundary === 'max' ? '12-31' : '01-01'}`;

  const parsedDate = moment(value, DATE_INPUT_FORMATS, true);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : value;
};

const ConfirmingPickersActionBar = ({ onAccept, onConfirmAccept, ...actionBarProps }) => {
  const handleAccept = event => {
    onConfirmAccept();
    onAccept(event);
  };

  return <PickersActionBar {...actionBarProps} onAccept={handleAccept} />;
};

ConfirmingPickersActionBar.propTypes = {
  onAccept: PropTypes.func.isRequired,
  onConfirmAccept: PropTypes.func.isRequired,
};

const styles = theme => ({
  dateInputWrapper: {
    display: 'inline-flex',
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
  },
  root: {
    display: 'flex',
    position: 'relative',
    flexWrap: 'wrap',
    height: 48,
    margin: '0',
    fontFamily: theme.typography.fontFamily,
    backgroundColor: '#F6F5FA',
  },
  textFieldFocus: {},
  inputRoot: {
    width: '100%',
    margin: 'auto',
    padding: '0px 7px',
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
  },
  inputNoShrink: {},
  inputHidePlaceholder: {
    '& input': {
      color: 'transparent',
      WebkitTextFillColor: 'transparent',
    },
    '& input::selection': {
      color: 'transparent',
      backgroundColor: 'transparent',
    },
    '& input::placeholder': {
      color: 'transparent',
      opacity: 0,
    },
  },
  formLabel: {
    color: theme.colors.eyConfidentGrey,
    paddingLeft: '8px',
    fontFamily: theme.typography.fontFamily,
    display: 'block',
    // maxWidth: 'calc(100% - 32px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  formControl: {
    transform: 'translate(0, 14px) scale(1)',
    // width: 'calc(100% - 8px)',
  },
  noShrink: {
    maxWidth: 'calc(100% - 32px)',
  },
  shrink: {
    transform: 'translate(0, 5px) scale(0.75)',
    marginBottom: '8px',
    color: theme.colors.eyGrey1,
    width: 'calc(133% - 8px)',
  },
  calendarTodayIcon: {
    color: theme.colors.eyGrey1,
    padding: 4,
    top: -6,
    right: 6,
    '&:hover, &:focus': {
      // backgroundColor: 'transparent',
      backgroundColor: '#c4c4c4 !important',
    },
    '& svg': {
      color: theme.colors.eyGrey1,
      fontSize: 20,
    },
  },
  focused: {
    '&$focused': {
      color: theme.colors.eyGrey1,
    },
  },
  hasError: {},
});

const DateInputBase = props => {
  const {
    classes,
    id,
    name,
    onChange,
    okToConfirm = false,
    className,
    disableUnderline,
    hasError,
    value,
    label,
    required,
    InputLabelProps,
    format,
    min,
    max,
    minDate,
    maxDate,
    inputProps,
    disabled,
    readOnly,
    inputMode,
    editable,
    disableTyping,
    stablePopper = false,
    ...otherProps
  } = props;

  const isReadOnly = readOnly || (inputProps && inputProps.readOnly);
  const resolvedInputMode = editable || inputMode === 'editable' || disableTyping === false ? 'editable' : 'picker';
  const isTypingEnabled = resolvedInputMode === 'editable' && !isReadOnly;
  const selectedDate = getParsedDate(value);
  const minValue = normalizeDateBoundaryValue(min || minDate, 'min');
  const maxValue = normalizeDateBoundaryValue(max || maxDate, 'max');
  const minMoment = minValue ? moment(minValue, 'YYYY-MM-DD', true).startOf('day') : null;
  const maxMoment = maxValue ? moment(maxValue, 'YYYY-MM-DD', true).startOf('day') : null;
  const pickerFormat = format || 'DD-MMM-YYYY';
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const [pendingDate, setPendingDate] = useState(null);
  const [popperPlacement, setPopperPlacement] = useState('bottom-start');
  const inputRef = useRef(null);
  const acceptConfirmedRef = useRef(false);
  const pickerValue = okToConfirm && isPickerOpen ? pendingDate : selectedDate;
  const labelText = required ? label + intl.get('questionnaire.requiredLabel') : label;
  const shouldShrinkLabel = !!selectedDate || (isTypingEnabled && isFieldFocused);
  const shouldHidePlaceholder = !selectedDate && (!isTypingEnabled || !isFieldFocused);

  const commitDateChange = date => {
    if (!onChange) return;

    if (!date || !date.isValid()) {
      onChange('', name);
      return;
    }

    onChange(date.format(pickerFormat), name);
  };

  const handlePickerChange = (date, context) => {
    if (context && context.validationError) return;

    if (okToConfirm) {
      setPendingDate(date);
      return;
    }

    commitDateChange(date);
  };

  const handlePickerAccept = date => {
    if (!okToConfirm) return;
    if (!acceptConfirmedRef.current) return;

    acceptConfirmedRef.current = false;
    setIsPickerOpen(false);
    setPendingDate(null);
    commitDateChange(date);
  };

  const resolvePopperPlacement = () => {
    const anchor = inputRef.current;
    if (!anchor) return 'bottom-start';

    // Open upward only when there isn't enough room below for the calendar (~360px).
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    return spaceBelow < 360 && rect.top > spaceBelow ? 'top-start' : 'bottom-start';
  };

  const openDatePicker = () => {
    if (disabled || isReadOnly) return;

    acceptConfirmedRef.current = false;
    if (okToConfirm) {
      setPendingDate(selectedDate);
    }

    if (stablePopper) setPopperPlacement(resolvePopperPlacement());
    setIsPickerOpen(true);
  };

  const handleTextFieldFocus = event => {
    setIsFieldFocused(true);
    if (otherProps.onFocus) otherProps.onFocus(event);
  };

  const handleTextFieldBlur = event => {
    setIsFieldFocused(false);
    if (otherProps.onBlur) otherProps.onBlur(event);
  };

  const handleTextFieldClick = event => {
    if (otherProps.onClick) otherProps.onClick(event);
    if (!isTypingEnabled && !event.defaultPrevented) openDatePicker();
  };

  const clearPickerOnlyFocus = () => {
    if (isTypingEnabled) return;

    setIsFieldFocused(false);
    const clearInputFocus = () => {
      if (!inputRef.current) return;

      inputRef.current.setSelectionRange(0, 0);
      inputRef.current.blur();
    };

    window.requestAnimationFrame(clearInputFocus);
    window.setTimeout(clearInputFocus, 0);
    window.setTimeout(clearInputFocus, 50);
  };

  const handlePickerClose = () => {
    acceptConfirmedRef.current = false;
    setIsPickerOpen(false);
    setPendingDate(null);
    clearPickerOnlyFocus();
  };

  delete otherProps.onFocus;
  delete otherProps.onBlur;
  delete otherProps.onClick;

  return (
    <div className={classes.dateInputWrapper + (className ? ` ${className}` : '')}>
      <LocalizationProvider dateAdapter={AdapterMoment} localeText={DATE_INPUT_LOCALE_TEXT}>
        <MuiDatePicker
          value={pickerValue}
          onChange={handlePickerChange}
          onAccept={handlePickerAccept}
          format={pickerFormat}
          minDate={minMoment || undefined}
          maxDate={maxMoment || undefined}
          disabled={disabled}
          readOnly={isReadOnly}
          open={isPickerOpen}
          onOpen={openDatePicker}
          onClose={handlePickerClose}
          closeOnSelect={okToConfirm ? false : undefined}
          slotProps={{
            field: {
              readOnly: !isTypingEnabled,
            },
            textField: {
              ...otherProps,
              variant: 'standard',
              id,
              name,
              label: labelText,
              error: hasError,
              disabled,
              className: classNames({
                [classes.root]: true,
                [classes.textFieldFocus]: isFieldFocused,
                [classes.hasError]: hasError,
              }),
              onFocus: handleTextFieldFocus,
              onBlur: handleTextFieldBlur,
              onClick: handleTextFieldClick,
              inputRef,
              InputProps: {
                disableUnderline,
                className: classNames(classes.inputRoot, !shouldShrinkLabel && classes.inputNoShrink, shouldHidePlaceholder && classes.inputHidePlaceholder),
              },
              inputProps: {
                ...inputProps,
                className: inputProps && inputProps.className,
                placeholder: '',
                readOnly: !isTypingEnabled,
              },
              InputLabelProps: {
                shrink: shouldShrinkLabel,
                classes: {
                  formControl: classNames(classes.formControl, !shouldShrinkLabel && classes.noShrink),
                  shrink: classes.shrink,
                  root: classes.formLabel,
                  focused: classes.focused,
                },
                ...InputLabelProps,
              },
            },
            openPickerButton: {
              className: classes.calendarTodayIcon,
            },
            ...(stablePopper
              ? {
            popper: {
              // Placement is chosen once on open
              // flip/preventOverflow are disabled so the calendar can't flicker up and down.
              placement: popperPlacement,
              modifiers: [
                { name: 'flip', enabled: false },
                { name: 'preventOverflow', enabled: false },
              ],
            },
                }
              : {}),
            ...(okToConfirm
              ? {
                  actionBar: {
                    actions: ['cancel', 'accept'],
                    onConfirmAccept: () => {
                      acceptConfirmedRef.current = true;
                    },
                  },
                }
              : {}),
          }}
          slots={
            okToConfirm
              ? {
                  actionBar: ConfirmingPickersActionBar,
                }
              : undefined
          }
        />
      </LocalizationProvider>
    </div>
  );
};

DateInputBase.propTypes = {
  classes: PropTypes.object.isRequired,
  /** Classname to be applied to <TextField> */
  className: PropTypes.string,
  id: PropTypes.string,
  /** If `true`, the input will not have an underline. */
  disableUnderline: PropTypes.bool,
  /** If `true`, textfield will be in error state */
  hasError: PropTypes.bool,
  /** Override or extend default styles */
  styles: PropTypes.object,
  /** Name attribute of the `input` element. */
  name: PropTypes.string.isRequired,
  /** Value of `input` element */
  value: PropTypes.any,
  /** Text used for input label */
  label: PropTypes.string,
  InputLabelProps: PropTypes.object,
  inputProps: PropTypes.object,
  format: PropTypes.string,
  min: PropTypes.any,
  max: PropTypes.any,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  /** Use "editable" to allow typing, or "picker" to select only from the popup. */
  inputMode: PropTypes.oneOf(['editable', 'picker']),
  /** Alias for inputMode="editable". */
  editable: PropTypes.bool,
  /** If true, the text field opens the picker but does not allow typing. */
  disableTyping: PropTypes.bool,
  /** If `true`, the label is displayed as required. */
  required: PropTypes.any,
  /** Callback fired when value is changed */
  onChange: PropTypes.func,
  /** If `true`, date changes are applied only after clicking OK in the picker. */
  okToConfirm: PropTypes.bool,
  /** If `true`, opens the calendar up/down based on space and prevents the popper flicker. */
  stablePopper: PropTypes.bool,
};

export default styled(DateInputBase)(styles, { name: 'DateInputBase' });